import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Settings as SettingsIcon, Save, Sparkles, MessageSquare, Bot } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { WsPageTitle } from "./WorkspaceLayout";
import { ContactTab } from "../admin/Settings";
import NovaProvidersCard from "../../components/admin/NovaProvidersCard";

const SECTIONS = [
  ["dashboard", "Panel"],
  ["notlar", "Notlar"],
  ["yazilar", "Yazılarım"],
  ["gorevler", "Yapılacaklar"],
  ["belgeler", "Belgelerim"],
  ["alintilar", "Alıntılarım"],
];
const ACCENTS = [
  ["indigo", "Mor", "from-indigo-500 to-indigo-700"],
  ["violet", "Mor+Pembe", "from-violet-500 to-fuchsia-600"],
  ["emerald", "Yeşil", "from-emerald-500 to-emerald-700"],
  ["rose", "Pembe", "from-rose-500 to-rose-700"],
  ["amber", "Amber", "from-amber-500 to-orange-600"],
  ["sky", "Mavi", "from-sky-500 to-sky-700"],
];

function WorkspacePrefs() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["workspace", "settings"],
    queryFn: () => api.get("/admin/workspace/settings").then((r) => r.data),
  });

  const [defaultSection, setDefaultSection] = useState("dashboard");
  const [accent, setAccent] = useState("indigo");
  const [startOnWs, setStartOnWs] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      const bs = data.default_section;
      const bsSlug = {
        dashboard: "dashboard", notes: "notlar", writings: "yazilar",
        todos: "gorevler", documents: "belgeler", quotes: "alintilar",
      }[bs] || "dashboard";
      setDefaultSection(bsSlug);
      setAccent(data.accent || "indigo");
      setStartOnWs(!!data.start_on_workspace);
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const backendSection = {
        dashboard: "dashboard", notlar: "notes", yazilar: "writings",
        gorevler: "todos", belgeler: "documents", alintilar: "quotes",
      }[defaultSection] || "dashboard";
      await api.put("/admin/workspace/settings", {
        default_section: backendSection, accent, start_on_workspace: startOnWs,
      });
      toast.success("Kaydedildi");
      qc.invalidateQueries({ queryKey: ["workspace", "settings"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  if (!data) return <div className="text-sm text-stone-400">Yükleniyor…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <section className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm">
        <p className="font-semibold text-slate-900 mb-1">Başlangıç davranışı</p>
        <p className="text-xs text-stone-500 mb-3">Girişten sonra hangi sayfanın açılacağı.</p>
        <div className="flex items-center gap-3">
          <Switch checked={startOnWs} onCheckedChange={setStartOnWs} data-testid="ws-start-switch" />
          <span className="text-sm">
            {startOnWs ? "Login sonrası Workspace açılacak" : "Login sonrası Site Management (admin paneli) açılacak"}
          </span>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm">
        <p className="font-semibold text-slate-900 mb-1">Varsayılan bölüm</p>
        <p className="text-xs text-stone-500 mb-3">Workspace'e girince ilk görünecek sayfa (ileride etkinleşir).</p>
        <select
          value={defaultSection}
          onChange={(e) => setDefaultSection(e.target.value)}
          className="w-full border rounded-md px-3 py-2 text-sm bg-white"
          data-testid="ws-default-section"
        >
          {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </section>

      <section className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm">
        <p className="font-semibold text-slate-900 mb-1">Vurgu rengi</p>
        <p className="text-xs text-stone-500 mb-3">Workspace'in genel aksan rengi (başlıklar, butonlar).</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {ACCENTS.map(([v, l, g]) => (
            <button
              key={v}
              onClick={() => setAccent(v)}
              className={`relative overflow-hidden rounded-xl p-3 text-white shadow-sm hover:shadow-md transition-all bg-gradient-to-br ${g} ${accent === v ? "ring-4 ring-offset-2 ring-indigo-400" : ""}`}
              data-testid={`ws-accent-${v}`}
            >
              <p className="text-xs font-semibold">{l}</p>
            </button>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="ws-settings-save">
          <Save size={14} className="mr-1" />{saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}

export default function WorkspaceSettings() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "workspace";
  const setTab = (v) => { const n = new URLSearchParams(sp); n.set("tab", v); setSp(n, { replace: true }); };
  return (
    <div data-testid="ws-settings">
      <WsPageTitle icon={SettingsIcon} title="Workspace Ayarları" sub="Kişisel çalışma alanının, AI sağlayıcılarının ve iletişim kanallarının davranışı." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 bg-white border border-indigo-100">
          <TabsTrigger value="workspace" data-testid="ws-tab-workspace">
            <Sparkles size={13} className="mr-1" />Çalışma Alanı
          </TabsTrigger>
          <TabsTrigger value="nova" data-testid="ws-tab-nova">
            <Bot size={13} className="mr-1" />NovaAI Sağlayıcıları
          </TabsTrigger>
          <TabsTrigger value="communication" data-testid="ws-tab-communication">
            <MessageSquare size={13} className="mr-1" />İletişim & Şablonlar
          </TabsTrigger>
        </TabsList>
        <TabsContent value="workspace">
          <WorkspacePrefs />
        </TabsContent>
        <TabsContent value="nova">
          <NovaProvidersCard />
        </TabsContent>
        <TabsContent value="communication">
          <div className="space-y-5">
            <ContactTab />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
